import type { Prioridad } from "../../../shared/types";

// Lógica pura de la Cola de Trabajo de "Mi Trabajo": combina las distintas
// listas que ya devuelve GET /mi-trabajo (eventos/actuaciones del período,
// atrasados, y las categorías de Centro de Operaciones) en un único árbol
// expediente → ítems accionables. Cero fetches acá — todo llega ya resuelto
// del backend (ver functions/_app/routes/mi-trabajo.ts); esta capa sólo
// combina, deduplica, agrupa y traduce cada fila a "verbo + destino" según
// un diccionario fijo (nunca heurísticas ambiguas, ver DICCIONARIO_COLA).
// Extraído a un módulo aparte (sin JSX) para poder testearlo con el mismo
// patrón que src/lib/periodo.ts / colorSemantico.ts: funciones puras,
// inyectando "hoy" como parámetro en vez de leer Date.now() adentro.

export type CategoriaCola =
  | "vencimiento"
  | "recordatorio"
  | "audiencia"
  | "escrito"
  | "tarea"
  | "pericia"
  | "oficio"
  | "notificacion"
  | "cliente_espera"
  | "sin_movimiento"
  | "etapa_sin_responsable"
  | "etapa_vencida"
  | "etapa_por_vencer"
  | "honorario";

export type SeccionCola = "urgente" | "periodo";

// Mismas 8 pestañas de ExpedienteFichaPage.tsx (ver PESTANAS ahí) — sólo se
// usan las que la Cola de Trabajo puede necesitar como destino.
export type DestinoPestana = "datos" | "actuaciones" | "agenda" | "economico" | "proceso";

export interface ItemColaTrabajo {
  // Único en toda la cola: "<tabla origen>:<id de fila>" — la clave (no el
  // id solo) es lo que permite deduplicar entre categorías sin colisionar
  // ids de tablas distintas (eventos_agenda vs actuaciones vs honorario_cuotas).
  clave: string;
  categoria: CategoriaCola;
  seccion: SeccionCola;
  verbo: string;
  origenEtiqueta: string;
  destinoPestana: DestinoPestana;
  expedienteId: string | null;
  numeroExpediente: string | null;
  clienteNombre: string | null;
  descripcion: string;
  fecha: string | null;
  prioridad: Prioridad;
  responsableId: string | null;
  // responsableNombre/areaPracticaNombre: sólo poblados para los ítems que
  // vienen de Centro de Operaciones (urgente/atrasados) — ver
  // routes/mi-trabajo.ts y functions/_app/lib/centro-operaciones.ts, que ya
  // resuelven ambos nombres con un LEFT JOIN. Los ítems de período (agenda/
  // actuaciones propias) no los traen porque en Mi Trabajo el responsable ya
  // es siempre "vos" — quedan undefined y simplemente no se muestran.
  responsableNombre?: string | null;
  areaPracticaNombre?: string | null;
  // true = nunca se esconde detrás de un "ver más": vencidos, caducidades,
  // prioridad alta, o dentro de las próximas 48hs (ver spec §3).
  siempreVisible: boolean;
  // Bloque "Acciones rápidas de vencimientos": id real de `eventos_agenda` y
  // su estado crudo (no calculado), sólo poblados para categoria="vencimiento"
  // (normalizarEvento/normalizarCaducidadUrgente, ambos originados en esa
  // tabla) — null para el resto. Se exponen como campos propios en vez de
  // parsearlos de `clave` para no depender de un formato de string interno
  // como si fuera un contrato.
  agendaId: string | null;
  estadoAgenda: string | null;
}

export interface GrupoColaTrabajo {
  // null = ítems sin expediente asociado (defensivo: el modelo de datos
  // actual siempre trae expediente_id en cada fuente, así que este caso no
  // ocurre hoy en la práctica — ver reporte de entrega, punto 11).
  expedienteId: string | null;
  numeroExpediente: string | null;
  clienteNombre: string | null;
  responsableId: string | null;
  // Mismo criterio que clienteNombre: el primer valor no nulo encontrado
  // entre los ítems del grupo (ver agruparPorExpediente). Sólo tiene
  // contenido real cuando el rediseño de Centro de Operaciones invoca este
  // agrupador con datos que ya incluyen esta resolución (LEFT JOIN
  // usuarios/areas_practica) — Mi Trabajo no los necesita porque el
  // responsable ya es implícito ("vos").
  responsableNombre: string | null;
  areaPracticaNombre: string | null;
  prioridadMaxima: Prioridad;
  items: ItemColaTrabajo[];
}

// -- Diccionario verbo/destino por categoría (spec §4/§5) --------------------
// "el tipo real del dato" = tipo_evento de eventos_agenda, tipo de
// actuaciones, o la categoría de alerta operativa de Centro de Operaciones
// que originó la fila — nunca un heurístico ambiguo sobre el texto libre.
const DICCIONARIO_COLA: Record<CategoriaCola, { verbo: string; destino: DestinoPestana; etiqueta: string }> = {
  vencimiento: { verbo: "Cumplir", destino: "agenda", etiqueta: "Vencimiento" },
  recordatorio: { verbo: "Llamar", destino: "agenda", etiqueta: "Recordatorio" },
  audiencia: { verbo: "Preparar", destino: "actuaciones", etiqueta: "Audiencia" },
  escrito: { verbo: "Presentar", destino: "actuaciones", etiqueta: "Escrito" },
  tarea: { verbo: "Resolver", destino: "actuaciones", etiqueta: "Tarea" },
  pericia: { verbo: "Controlar", destino: "actuaciones", etiqueta: "Pericia" },
  oficio: { verbo: "Controlar", destino: "actuaciones", etiqueta: "Oficio" },
  notificacion: { verbo: "Cumplir", destino: "actuaciones", etiqueta: "Notificación" },
  cliente_espera: { verbo: "Responder", destino: "actuaciones", etiqueta: "Cliente esperando respuesta" },
  sin_movimiento: { verbo: "Controlar", destino: "datos", etiqueta: "Sin movimiento" },
  etapa_sin_responsable: { verbo: "Resolver", destino: "proceso", etiqueta: "Etapa sin responsable" },
  etapa_vencida: { verbo: "Cumplir", destino: "proceso", etiqueta: "Etapa vencida" },
  etapa_por_vencer: { verbo: "Cumplir", destino: "proceso", etiqueta: "Etapa por vencer" },
  honorario: { verbo: "Cobrar", destino: "economico", etiqueta: "Honorario" },
};

const RANGO_PRIORIDAD: Record<Prioridad, number> = { Alta: 3, Media: 2, Baja: 1 };

export function prioridadMasAlta(a: Prioridad, b: Prioridad): Prioridad {
  return RANGO_PRIORIDAD[a] >= RANGO_PRIORIDAD[b] ? a : b;
}

// Construye el link real a la ficha del expediente reusando EXACTAMENTE el
// mecanismo que ya usa ExpedienteFichaPage.tsx (?tab=, vía useEstadoEnUrl) —
// nunca una ruta nueva.
export function enlaceExpediente(expedienteId: string, destino: DestinoPestana): string {
  return `/expedientes/${expedienteId}?tab=${destino}`;
}

// -- Acciones rápidas de vencimientos (bloque "Acciones rápidas desde Mi
// Trabajo") -----------------------------------------------------------------
// Mismo estado destino que ya acepta PATCH /agenda/:id/estado
// (functions/_app/routes/agenda.ts:234) — nunca "Vencido", que ahí también
// es siempre calculado, nunca un estado que se pueda escribir.
export type EstadoDestinoAgenda = "Cumplido" | "Cancelado" | "Pendiente";

// Transición exacta ya verificada en AgendaPage.tsx (columna "Acciones" de la
// tabla): Pendiente/Vencido → Cumplir+Cancelar; Cumplido/Cancelado → Reabrir
// (a Pendiente). Portada acá tal cual, sin reinterpretarla, para que Mi
// Trabajo/Centro de Operaciones ofrezcan exactamente las mismas transiciones
// que ya existen en Agenda — nunca una regla nueva.
//
// Sólo aplica a categoria="vencimiento" (agendaId no nulo): actuaciones,
// etapas, honorarios y "sin movimiento" no tienen una transición de estado
// de Agenda que ofrecer acá.
export function accionesVencimientoDisponibles(item: ItemColaTrabajo): EstadoDestinoAgenda[] {
  if (item.categoria !== "vencimiento" || !item.agendaId) return [];
  if (item.estadoAgenda === "Pendiente" || item.estadoAgenda === "Vencido") return ["Cumplido", "Cancelado"];
  if (item.estadoAgenda === "Cumplido" || item.estadoAgenda === "Cancelado") return ["Pendiente"];
  return [];
}

function sumarDias(fechaIso: string, dias: number): string {
  const [anio, mes, dia] = fechaIso.split("-").map(Number);
  const fecha = new Date(Date.UTC(anio, mes - 1, dia));
  fecha.setUTCDate(fecha.getUTCDate() + dias);
  return fecha.toISOString().substring(0, 10);
}

// "Nunca oculto": vencido, prioridad Alta, o cae dentro de las próximas 48hs
// — mismo umbral que ya usa el backend para `ItemCaducidad.critica` (ver
// centro-operaciones.ts), aplicado acá de forma uniforme a cualquier ítem
// con fecha, para que el criterio de "siempre visible" sea consistente en
// toda la cola y no sólo para caducidades.
function calcularSiempreVisible(seccion: SeccionCola, prioridad: Prioridad, fecha: string | null, limite48h: string): boolean {
  if (seccion === "urgente") return true;
  if (prioridad === "Alta") return true;
  if (!fecha) return false;
  return fecha <= limite48h;
}

// -- Formas de entrada (tal como las devuelve GET /mi-trabajo) --------------

export interface CaducidadApi {
  id: string;
  expediente_id: string;
  numero_expediente: string;
  cliente_nombre: string;
  titulo: string;
  fecha_vencimiento: string;
  estado: string;
  prioridad: Prioridad;
  responsable_id: string | null;
  // Opcionales: sólo presentes cuando el origen es
  // obtenerCentroOperaciones() (ya resuelve ambos con LEFT JOIN).
  responsable_nombre?: string | null;
  area_practica_nombre?: string | null;
  critica: boolean;
}

export interface ActuacionPendienteApi {
  id: string;
  expediente_id: string;
  numero_expediente: string;
  cliente_nombre: string;
  descripcion: string;
  fecha: string;
  prioridad: Prioridad;
  responsable_id: string | null;
  responsable_nombre?: string | null;
  area_practica_nombre?: string | null;
}

export interface SinMovimientoApi {
  id: string;
  numero_expediente: string;
  cliente_nombre: string;
  prioridad: Prioridad;
  ultima_actuacion: string | null;
  creado_en: string;
  responsable_id?: string | null;
  responsable_nombre?: string | null;
  area_practica_nombre?: string | null;
}

export interface EtapaProcesoApi {
  id: string;
  numero_expediente: string;
  cliente_nombre: string;
  prioridad: Prioridad;
  proceso_nombre: string;
  etapa_nombre: string;
  etapa_desde: string;
  dias_en_etapa: number;
  tiempo_objetivo_dias: number | null;
  etapa_responsable_id: string | null;
  etapa_responsable_nombre?: string | null;
  area_practica_nombre?: string | null;
}

export interface EventoAgendaApi {
  id: string;
  expediente_id: string;
  numero_expediente: string;
  cliente_nombre?: string | null;
  tipo_evento: string;
  titulo: string;
  fecha_vencimiento: string;
  prioridad: Prioridad;
  estado: string;
  responsable_id: string | null;
}

export interface ActuacionApi {
  id: string;
  expediente_id: string;
  numero_expediente: string;
  cliente_nombre?: string | null;
  tipo: string;
  descripcion: string;
  fecha: string;
  prioridad: Prioridad;
  responsable_id: string | null;
}

export interface HonorarioPendienteApi {
  id: string;
  expediente_id: string;
  numero_expediente: string;
  cliente_nombre: string;
  moneda: string;
  importe_total: number;
  vencimiento: string;
}

export interface DatosColaTrabajo {
  atrasados: EventoAgendaApi[];
  eventosPeriodo: EventoAgendaApi[];
  actuacionesPeriodo: ActuacionApi[];
  caducidades: CaducidadApi[];
  escritosPendientes: ActuacionPendienteApi[];
  clientesEsperandoRespuesta: ActuacionPendienteApi[];
  periciasPendientes: ActuacionPendienteApi[];
  oficiosSinRespuesta: ActuacionPendienteApi[];
  notificacionesPendientes: ActuacionPendienteApi[];
  expedientesSinMovimiento: SinMovimientoApi[];
  etapasSinResponsable: EtapaProcesoApi[];
  etapasVencidas: EtapaProcesoApi[];
  etapasPorVencer: EtapaProcesoApi[];
  honorariosPendientesLista: HonorarioPendienteApi[];
}

function categoriaDeEvento(tipoEvento: string): CategoriaCola {
  return tipoEvento === "Recordatorio" ? "recordatorio" : "vencimiento";
}

function categoriaDeActuacion(tipo: string): CategoriaCola {
  if (tipo === "Audiencia") return "audiencia";
  if (tipo === "Escrito") return "escrito";
  if (tipo === "Pericia") return "pericia";
  if (tipo === "Oficio") return "oficio";
  if (tipo === "Cédula de Notificación") return "notificacion";
  return "tarea";
}

function normalizarEvento(ev: EventoAgendaApi, seccion: SeccionCola, limite48h: string): ItemColaTrabajo {
  const categoria = categoriaDeEvento(ev.tipo_evento);
  const info = DICCIONARIO_COLA[categoria];
  return {
    clave: `ea:${ev.id}`,
    categoria,
    seccion,
    verbo: info.verbo,
    origenEtiqueta: info.etiqueta,
    destinoPestana: info.destino,
    expedienteId: ev.expediente_id,
    numeroExpediente: ev.numero_expediente,
    clienteNombre: ev.cliente_nombre ?? null,
    descripcion: ev.titulo,
    fecha: ev.fecha_vencimiento,
    prioridad: ev.prioridad,
    responsableId: ev.responsable_id,
    siempreVisible: calcularSiempreVisible(seccion, ev.prioridad, ev.fecha_vencimiento, limite48h),
    agendaId: ev.id,
    estadoAgenda: ev.estado,
  };
}

function normalizarCaducidadUrgente(c: CaducidadApi): ItemColaTrabajo {
  const info = DICCIONARIO_COLA.vencimiento;
  return {
    clave: `ea:${c.id}`,
    categoria: "vencimiento",
    seccion: "urgente",
    verbo: info.verbo,
    origenEtiqueta: info.etiqueta,
    destinoPestana: info.destino,
    expedienteId: c.expediente_id,
    numeroExpediente: c.numero_expediente,
    clienteNombre: c.cliente_nombre,
    descripcion: c.titulo,
    fecha: c.fecha_vencimiento,
    prioridad: c.prioridad,
    responsableId: c.responsable_id,
    responsableNombre: c.responsable_nombre,
    areaPracticaNombre: c.area_practica_nombre,
    siempreVisible: true,
    agendaId: c.id,
    estadoAgenda: c.estado,
  };
}

function normalizarActuacionUrgente(a: ActuacionPendienteApi, categoria: CategoriaCola): ItemColaTrabajo {
  const info = DICCIONARIO_COLA[categoria];
  return {
    clave: `act:${a.id}`,
    categoria,
    seccion: "urgente",
    verbo: info.verbo,
    origenEtiqueta: info.etiqueta,
    destinoPestana: info.destino,
    expedienteId: a.expediente_id,
    numeroExpediente: a.numero_expediente,
    clienteNombre: a.cliente_nombre,
    descripcion: a.descripcion,
    fecha: a.fecha,
    prioridad: a.prioridad,
    responsableId: a.responsable_id,
    responsableNombre: a.responsable_nombre,
    areaPracticaNombre: a.area_practica_nombre,
    siempreVisible: true,
    agendaId: null,
    estadoAgenda: null,
  };
}

function normalizarActuacionPeriodo(a: ActuacionApi, limite48h: string): ItemColaTrabajo {
  const categoria = categoriaDeActuacion(a.tipo);
  const info = DICCIONARIO_COLA[categoria];
  return {
    clave: `act:${a.id}`,
    categoria,
    seccion: "periodo",
    verbo: info.verbo,
    origenEtiqueta: info.etiqueta,
    destinoPestana: info.destino,
    expedienteId: a.expediente_id,
    numeroExpediente: a.numero_expediente,
    clienteNombre: a.cliente_nombre ?? null,
    descripcion: a.descripcion,
    fecha: a.fecha,
    prioridad: a.prioridad,
    responsableId: a.responsable_id,
    siempreVisible: calcularSiempreVisible("periodo", a.prioridad, a.fecha, limite48h),
    agendaId: null,
    estadoAgenda: null,
  };
}

function normalizarSinMovimiento(s: SinMovimientoApi): ItemColaTrabajo {
  const info = DICCIONARIO_COLA.sin_movimiento;
  // ultima_actuacion/creado_en son columnas datetime ("AAAA-MM-DD HH:MM:SS",
  // ver MAX(a.creado_en)/e.creado_en en centro-operaciones.ts) — acá sólo
  // interesa el día, tal como asume el resto de la cola (fecha se compara
  // como string contra hoyIso "AAAA-MM-DD" y se resta en diasEntre() en
  // ColaTrabajoVista.tsx). Sin este recorte, la parte de hora rompía
  // diasEntre() ("Vencido hace NaN días") y la descripción mostraba el
  // timestamp crudo en vez de una fecha.
  const ultima = (s.ultima_actuacion ?? s.creado_en).slice(0, 10);
  return {
    clave: `exp:${s.id}`,
    categoria: "sin_movimiento",
    seccion: "urgente",
    verbo: info.verbo,
    origenEtiqueta: info.etiqueta,
    destinoPestana: info.destino,
    expedienteId: s.id,
    numeroExpediente: s.numero_expediente,
    clienteNombre: s.cliente_nombre,
    descripcion: `Sin actuaciones desde ${ultima}`,
    fecha: ultima,
    prioridad: s.prioridad,
    responsableId: s.responsable_id ?? null,
    responsableNombre: s.responsable_nombre,
    areaPracticaNombre: s.area_practica_nombre,
    siempreVisible: true,
    agendaId: null,
    estadoAgenda: null,
  };
}

function normalizarEtapa(e: EtapaProcesoApi, categoria: "etapa_sin_responsable" | "etapa_vencida" | "etapa_por_vencer"): ItemColaTrabajo {
  const info = DICCIONARIO_COLA[categoria];
  return {
    // Prefijo por categoría (no sólo por id): la misma etapa puede aparecer
    // a la vez en "sin responsable" y en "vencida" — son dos problemas
    // distintos sobre la misma fila, no un duplicado del mismo hecho.
    clave: `etapa:${categoria}:${e.id}`,
    categoria,
    seccion: "urgente",
    verbo: info.verbo,
    origenEtiqueta: info.etiqueta,
    destinoPestana: info.destino,
    expedienteId: e.id,
    numeroExpediente: e.numero_expediente,
    clienteNombre: e.cliente_nombre,
    descripcion: `${e.proceso_nombre} · ${e.etapa_nombre} (${e.dias_en_etapa} días)`,
    fecha: e.etapa_desde,
    prioridad: e.prioridad,
    responsableId: e.etapa_responsable_id,
    responsableNombre: e.etapa_responsable_nombre,
    areaPracticaNombre: e.area_practica_nombre,
    siempreVisible: true,
    agendaId: null,
    estadoAgenda: null,
  };
}

// honorario_cuotas no tiene columna de prioridad propia (a diferencia de
// actuaciones/eventos_agenda) — "Media" es un default neutro, sólo para que
// no distorsione el cálculo de prioridad máxima del grupo; no representa
// ningún juicio de negocio nuevo.
function normalizarHonorario(h: HonorarioPendienteApi, limite48h: string): ItemColaTrabajo {
  const info = DICCIONARIO_COLA.honorario;
  const prioridad: Prioridad = "Media";
  return {
    clave: `hc:${h.id}`,
    categoria: "honorario",
    seccion: "periodo",
    verbo: info.verbo,
    origenEtiqueta: info.etiqueta,
    destinoPestana: info.destino,
    expedienteId: h.expediente_id,
    numeroExpediente: h.numero_expediente,
    clienteNombre: h.cliente_nombre,
    descripcion: `${h.moneda} ${h.importe_total.toLocaleString("es-AR")}`,
    fecha: h.vencimiento,
    prioridad,
    responsableId: null,
    siempreVisible: calcularSiempreVisible("periodo", prioridad, h.vencimiento, limite48h),
    agendaId: null,
    estadoAgenda: null,
  };
}

function agruparPorExpediente(items: ItemColaTrabajo[]): GrupoColaTrabajo[] {
  const grupos = new Map<string, GrupoColaTrabajo>();
  for (const item of items) {
    const clave = item.expedienteId ?? "__sin_expediente__";
    let grupo = grupos.get(clave);
    if (!grupo) {
      grupo = {
        expedienteId: item.expedienteId,
        numeroExpediente: item.numeroExpediente,
        clienteNombre: item.clienteNombre,
        responsableId: item.responsableId,
        responsableNombre: item.responsableNombre ?? null,
        areaPracticaNombre: item.areaPracticaNombre ?? null,
        prioridadMaxima: item.prioridad,
        items: [],
      };
      grupos.set(clave, grupo);
    }
    grupo.items.push(item);
    grupo.prioridadMaxima = prioridadMasAlta(grupo.prioridadMaxima, item.prioridad);
    if (!grupo.responsableId && item.responsableId) grupo.responsableId = item.responsableId;
    if (!grupo.responsableNombre && item.responsableNombre) grupo.responsableNombre = item.responsableNombre;
    if (!grupo.areaPracticaNombre && item.areaPracticaNombre) grupo.areaPracticaNombre = item.areaPracticaNombre;
    if (!grupo.numeroExpediente && item.numeroExpediente) grupo.numeroExpediente = item.numeroExpediente;
    if (!grupo.clienteNombre && item.clienteNombre) grupo.clienteNombre = item.clienteNombre;
  }

  const resultado = [...grupos.values()];
  for (const g of resultado) {
    g.items.sort((a, b) => {
      if (a.seccion !== b.seccion) return a.seccion === "urgente" ? -1 : 1;
      return (a.fecha ?? "").localeCompare(b.fecha ?? "");
    });
  }
  resultado.sort((a, b) => {
    const rangoA = RANGO_PRIORIDAD[a.prioridadMaxima];
    const rangoB = RANGO_PRIORIDAD[b.prioridadMaxima];
    if (rangoA !== rangoB) return rangoB - rangoA;
    const fechaA = a.items[0]?.fecha ?? "";
    const fechaB = b.items[0]?.fecha ?? "";
    return fechaA.localeCompare(fechaB);
  });
  return resultado;
}

// Punto de entrada: combina TODAS las fuentes ya cargadas por GET
// /mi-trabajo en la cola agrupada por expediente. `hoyIso` se inyecta (no
// se lee Date.now() acá) para que la función sea 100% determinística y
// testeable, mismo patrón que calcularRangoPeriodo() en src/lib/periodo.ts.
export function construirColaTrabajo(datos: DatosColaTrabajo, hoyIso: string): GrupoColaTrabajo[] {
  const limite48h = sumarDias(hoyIso, 2);
  const items: ItemColaTrabajo[] = [];
  const clavesUrgentes = new Set<string>();

  // Sección A — eventos de agenda ya vencidos (cualquier tipo, sin tope de
  // 20 a diferencia de `caducidades`, ver atrasadosP en mi-trabajo.ts).
  for (const ev of datos.atrasados) {
    const item = normalizarEvento(ev, "urgente", limite48h);
    clavesUrgentes.add(item.clave);
    items.push(item);
  }
  // Caducidades críticas (vencidas o a ≤48hs) que `atrasados` no cubrió —
  // p.ej. una que vence pasado mañana, todavía no está "vencida".
  for (const c of datos.caducidades) {
    const clave = `ea:${c.id}`;
    if (clavesUrgentes.has(clave)) continue;
    if (!c.critica) continue;
    clavesUrgentes.add(clave);
    items.push(normalizarCaducidadUrgente(c));
  }

  // Sección A — actuaciones marcadas como críticas por Centro de
  // Operaciones. Un mismo id puede aparecer en más de una lista (ej. un
  // Escrito que además "requiere_respuesta_cliente"); se resuelve con
  // last-write-wins sobre un Map, en orden de precedencia creciente, para
  // que la categoría más específica/urgente gane sin duplicar la fila.
  const actuacionesUrgentes = new Map<string, ItemColaTrabajo>();
  const registrar = (lista: ActuacionPendienteApi[], categoria: CategoriaCola) => {
    for (const a of lista) actuacionesUrgentes.set(`act:${a.id}`, normalizarActuacionUrgente(a, categoria));
  };
  registrar(datos.escritosPendientes, "escrito");
  registrar(datos.periciasPendientes, "pericia");
  registrar(datos.oficiosSinRespuesta, "oficio");
  registrar(datos.notificacionesPendientes, "notificacion");
  registrar(datos.clientesEsperandoRespuesta, "cliente_espera");
  for (const [clave, item] of actuacionesUrgentes) {
    clavesUrgentes.add(clave);
    items.push(item);
  }

  // Sección A — expedientes sin movimiento y etapas de Proceso.
  for (const s of datos.expedientesSinMovimiento) items.push(normalizarSinMovimiento(s));
  for (const e of datos.etapasSinResponsable) items.push(normalizarEtapa(e, "etapa_sin_responsable"));
  for (const e of datos.etapasVencidas) items.push(normalizarEtapa(e, "etapa_vencida"));
  for (const e of datos.etapasPorVencer) items.push(normalizarEtapa(e, "etapa_por_vencer"));

  // Sección B — período elegido, excluyendo lo que ya quedó en Sección A
  // (evita el duplicado real: un vencimiento atrasado puede caer dentro de
  // `eventosPeriodo` si el usuario eligió un período que incluye fechas
  // pasadas).
  for (const ev of datos.eventosPeriodo) {
    if (clavesUrgentes.has(`ea:${ev.id}`)) continue;
    items.push(normalizarEvento(ev, "periodo", limite48h));
  }
  for (const a of datos.actuacionesPeriodo) {
    if (clavesUrgentes.has(`act:${a.id}`)) continue;
    items.push(normalizarActuacionPeriodo(a, limite48h));
  }
  for (const h of datos.honorariosPendientesLista) items.push(normalizarHonorario(h, limite48h));

  return agruparPorExpediente(items);
}

// Expuesta aparte para poder testear el caso "ítems sin expediente" de
// forma aislada (el modelo de datos actual no produce ninguno hoy, ver
// reporte de entrega) sin tener que fabricar una respuesta completa de
// GET /mi-trabajo.
export { agruparPorExpediente };

// -- Semáforo de "Carga del equipo" (spec §9) --------------------------------
// Lectura visual derivada de los mismos números que ya muestra la tabla
// (audiencias + escritos + tareas + vencimientos del período) — nunca se
// presenta como medición objetiva de capacidad, los números originales
// siguen visibles al lado del badge (ver CargaEquipoTabla en
// MiTrabajoPage.tsx). Umbrales deliberadamente simples, sin fórmula
// sofisticada, mismo espíritu que calcularDisponibilidad() del backend.
export type NivelCarga = "Baja" | "Media" | "Alta" | "Sobrecargada";

export function calcularNivelCarga(totalItems: number): NivelCarga {
  if (totalItems <= 3) return "Baja";
  if (totalItems <= 7) return "Media";
  if (totalItems <= 12) return "Alta";
  return "Sobrecargada";
}
